// Bugfix 30 (#1403): de builder van de Template Maker.
//
// Velden in een oefeningrij (sets, reps, tempo, rust) en de sessie-instellingen
// worden meteen opgeslagen met fetch en JSON, zodat de cursor blijft staan.
// Alles wat de structuur verandert (toevoegen, verwijderen, slepen, supersets)
// gaat als gewoon formulier via Turbo; het antwoord ververst de structuur en
// de sessie. De status bovenaan volgt beide.
import { Controller } from "stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = [
    "status", "statustekst", "voorbeeld", "titel", "bulk", "bulkTeller", "bulkIds", "vink", "lijst",
    "vervangen", "vervangenNaam", "overzicht"
  ]

  static values = { teksten: Object, template: String }

  connect() {
    this.bezig = 0
    this.opStart = () => this.zetStatus("bezig")
    this.opEind = (event) => {
      this.zetStatus(event.detail && event.detail.success === false ? "fout" : "klaar")
      if (this.hasVervangenTarget && !this.vervangenTarget.hidden) this.sluitVervangen()
    }
    this.opInklap = (event) => {
      const schema = event.target.closest && event.target.closest("details.tb-schema")
      if (schema && event.target === schema) this.bewaarInklap(schema)
    }
    this.opToets = (event) => {
      if (event.key !== "Escape") return
      if (this.hasVervangenTarget && !this.vervangenTarget.hidden) this.sluitVervangen()
      this.element.classList.remove("is-structuur-open", "is-bibliotheek-open")
    }
    this.opStream = (event) => {
      const origineel = event.detail.render
      event.detail.render = async (streamElement) => {
        await origineel(streamElement)
        this.naVerversen()
      }
    }

    this.element.addEventListener("turbo:submit-start", this.opStart)
    this.element.addEventListener("turbo:submit-end", this.opEind)
    this.element.addEventListener("toggle", this.opInklap, true)
    document.addEventListener("keydown", this.opToets)
    document.addEventListener("turbo:before-stream-render", this.opStream)
    this.herstelInklap()
    this.springNaarAnker()
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.opStart)
    this.element.removeEventListener("turbo:submit-end", this.opEind)
    this.element.removeEventListener("toggle", this.opInklap, true)
    document.removeEventListener("keydown", this.opToets)
    document.removeEventListener("turbo:before-stream-render", this.opStream)
  }

  // ---- status ------------------------------------------------------------

  zetStatus(stand) {
    if (!this.hasStatusTarget) return
    if (stand === "bezig") this.bezig += 1
    if (stand !== "bezig") this.bezig = Math.max(0, this.bezig - 1)
    const status = this.statusTarget
    status.classList.toggle("is-bezig", this.bezig > 0)
    status.classList.toggle("is-fout", stand === "fout")
    const teksten = this.tekstenValue || {}
    let tekst = teksten.opgeslagen
    if (this.bezig > 0) tekst = teksten.opslaan
    if (stand === "fout") tekst = teksten.fout
    if (this.hasStatustekstTarget) this.statustekstTarget.textContent = tekst
  }

  csrf() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  async verstuur(form, accept, body = new FormData(form)) {
    this.zetStatus("bezig")
    try {
      const antwoord = await fetch(form.action, {
        method: "POST",
        headers: { Accept: accept, "X-CSRF-Token": this.csrf(), "X-Requested-With": "XMLHttpRequest" },
        body,
        credentials: "same-origin"
      })
      if (!antwoord.ok) throw new Error(`HTTP ${antwoord.status}`)
      this.zetStatus("klaar")
      return antwoord
    } catch (fout) {
      this.zetStatus("fout")
      return null
    }
  }

  // ---- velden in een rij -------------------------------------------------

  async bewaarVeld(event) {
    const form = event.target.closest("form")
    if (!form || !event.target.name) return
    // Alleen het veld dat veranderde: stuur je alles mee, dan zet het oude
    // aantal sets een nieuwe setverdeling in reps weer terug.
    const body = new FormData()
    body.append("_method", "patch")
    body.append(event.target.name, event.target.value)
    const antwoord = await this.verstuur(form, "application/json", body)
    if (!antwoord) {
      event.target.classList.add("is-fout")
      return
    }
    event.target.classList.remove("is-fout")
    const data = await antwoord.json()
    const zet = (veld, waarde) => {
      const invoer = form.querySelector(`[data-veld="${veld}"]`)
      if (invoer && document.activeElement !== invoer) invoer.value = waarde === null || waarde === undefined ? "" : waarde
    }
    zet("sets", data.sets)
    zet("reps", data.reps)
    zet("tempo", data.tempo)
    zet("rest", data.rest)
    if (data.overzicht) {
      const oud = document.getElementById("tb-overzicht")
      if (oud) oud.outerHTML = data.overzicht
    }
  }

  async bewaarFormulier(event) {
    const form = event.target.closest("form")
    if (!form) return
    const antwoord = await this.verstuur(form, "application/json")
    if (!antwoord) return
    const data = await antwoord.json()
    if (this.hasTitelTarget && data.volledig) this.titelTarget.textContent = data.volledig
    const link = this.element.querySelector(`[data-sessie-link="${data.id}"] .tb-sessie-naam`)
    if (link && data.naam) link.textContent = data.naam
  }

  // ---- supersets ---------------------------------------------------------

  supersetAan(event) {
    const form = event.target.closest("form")
    const keuze = form.querySelector("select")
    if (!event.target.checked) {
      form.requestSubmit()
      return
    }
    keuze.hidden = false
    if (keuze.value) {
      form.requestSubmit()
    } else {
      keuze.focus()
    }
  }

  supersetKies(event) {
    const form = event.target.closest("form")
    const vink = form.querySelector('input[type="checkbox"]')
    vink.checked = event.target.value !== ""
    form.requestSubmit()
  }

  // ---- bewerken en vervangen ---------------------------------------------

  wisselBewerk(event) {
    const knop = event.currentTarget
    const paneel = document.getElementById(knop.getAttribute("aria-controls"))
    if (!paneel) return
    paneel.hidden = !paneel.hidden
    knop.setAttribute("aria-expanded", paneel.hidden ? "false" : "true")
  }

  openVervangen(event) {
    if (!this.hasVervangenTarget) return
    const rij = event.currentTarget.closest(".tb-rij")
    const url = rij.dataset.oefeningUrl
    this.vervangenTarget.querySelectorAll("form").forEach((form) => { form.action = url })
    if (this.hasVervangenNaamTarget) this.vervangenNaamTarget.textContent = rij.dataset.naam || ""
    this.vervangenTarget.hidden = false
    const zoek = this.vervangenTarget.querySelector('input[type="search"]')
    if (zoek) {
      zoek.value = ""
      zoek.dispatchEvent(new Event("input"))
      zoek.focus()
    }
  }

  sluitVervangen() {
    if (this.hasVervangenTarget) this.vervangenTarget.hidden = true
  }

  sluitVervangenBuiten(event) {
    if (event.target === this.vervangenTarget) this.sluitVervangen()
  }

  // ---- panelen op tablet -------------------------------------------------

  wisselStructuur() {
    this.element.classList.remove("is-bibliotheek-open")
    this.element.classList.toggle("is-structuur-open")
  }

  wisselBibliotheek() {
    this.element.classList.remove("is-structuur-open")
    this.element.classList.toggle("is-bibliotheek-open")
  }

  openBibliotheek() {
    if (window.innerWidth < 1440) {
      this.element.classList.remove("is-structuur-open")
      this.element.classList.add("is-bibliotheek-open")
    }
    const zoek = this.element.querySelector('.tb-rechts input[type="search"]')
    if (zoek) zoek.focus()
  }

  // ---- meerdere oefeningen tegelijk --------------------------------------

  selectie() {
    const gekozen = this.vinkTargets.filter((vink) => vink.checked)
    this.vinkTargets.forEach((vink) => vink.closest(".tb-rij").classList.toggle("is-gekozen", vink.checked))
    if (!this.hasBulkTarget) return
    this.bulkTarget.hidden = gekozen.length === 0
    if (this.hasBulkTellerTarget) {
      this.bulkTellerTarget.textContent = `${gekozen.length} ${(this.tekstenValue || {}).geselecteerd || ""}`
    }
  }

  wisSelectie() {
    this.vinkTargets.forEach((vink) => { vink.checked = false })
    this.selectie()
  }

  // "Kopiëren naar beneden": deze rij en alles eronder selecteren, met de
  // reps van deze rij alvast ingevuld.
  selecteerVanaf(event) {
    const rij = event.currentTarget.closest(".tb-rij")
    let aan = false
    this.vinkTargets.forEach((vink) => {
      if (vink.closest(".tb-rij") === rij) aan = true
      vink.checked = aan
    })
    this.selectie()
    if (!this.hasBulkTarget) return
    const veld = this.bulkTarget.querySelector("select")
    const waarde = this.bulkTarget.querySelector('input[name="waarde"]')
    const bron = rij.querySelector(`[data-veld="${veld.value}"]`)
    if (bron && waarde) waarde.value = bron.value
    veld.onchange = () => {
      const nieuweBron = rij.querySelector(`[data-veld="${veld.value}"]`)
      if (nieuweBron) waarde.value = nieuweBron.value
    }
    if (waarde) waarde.focus()
  }

  vulBulk() {
    if (!this.hasBulkIdsTarget) return
    this.bulkIdsTarget.innerHTML = ""
    this.vinkTargets.filter((vink) => vink.checked).forEach((vink) => {
      const invoer = document.createElement("input")
      invoer.type = "hidden"
      invoer.name = "ids[]"
      invoer.value = vink.value
      this.bulkIdsTarget.appendChild(invoer)
    })
  }

  // ---- slepen ------------------------------------------------------------

  // Alleen de greep is sleepbaar; zo blijft tekst in de velden selecteerbaar.
  // De hele groep (een superset als geheel) verhuist mee.
  sleepStart(event) {
    const groep = event.currentTarget.closest(".tb-groep")
    if (!groep) return
    this.gesleept = groep
    groep.classList.add("is-sleep")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", groep.dataset.ids || "")
    if (event.dataTransfer.setDragImage) event.dataTransfer.setDragImage(groep, 24, 24)
  }

  sleepOver(event) {
    if (!this.gesleept || event.currentTarget === this.gesleept) return
    event.preventDefault()
    const doel = event.currentTarget
    const vak = doel.getBoundingClientRect()
    const boven = event.clientY < vak.top + vak.height / 2
    doel.classList.toggle("is-doel-boven", boven)
    doel.classList.toggle("is-doel-onder", !boven)
  }

  sleepWeg(event) {
    event.currentTarget.classList.remove("is-doel-boven", "is-doel-onder")
  }

  async sleepNeer(event) {
    if (!this.gesleept || event.currentTarget === this.gesleept) return
    event.preventDefault()
    const doel = event.currentTarget
    const boven = doel.classList.contains("is-doel-boven")
    doel.classList.remove("is-doel-boven", "is-doel-onder")
    doel.parentNode.insertBefore(this.gesleept, boven ? doel : doel.nextSibling)
    this.gesleept.classList.remove("is-sleep")
    this.gesleept = null

    const lijst = this.lijstTarget
    const ids = []
    lijst.querySelectorAll(".tb-groep").forEach((groep) => {
      (groep.dataset.ids || "").split(",").filter(Boolean).forEach((id) => ids.push(id))
    })
    const form = document.createElement("form")
    form.action = lijst.dataset.volgordeUrl
    const methode = document.createElement("input")
    methode.type = "hidden"
    methode.name = "_method"
    methode.value = "patch"
    form.appendChild(methode)
    ids.forEach((id) => {
      const invoer = document.createElement("input")
      invoer.type = "hidden"
      invoer.name = "ids[]"
      invoer.value = id
      form.appendChild(invoer)
    })
    const antwoord = await this.verstuur(form, "text/vnd.turbo-stream.html")
    if (antwoord) Turbo.renderStreamMessage(await antwoord.text())
  }

  sleepEind() {
    if (this.gesleept) this.gesleept.classList.remove("is-sleep")
    this.gesleept = null
  }

  // ---- na een Turbo Stream -----------------------------------------------

  naVerversen() {
    this.herstelInklap()
    const midden = document.getElementById("tb-midden")
    if (midden && this.hasVoorbeeldTarget && midden.dataset.voorbeeldUrl) {
      this.voorbeeldTarget.href = midden.dataset.voorbeeldUrl
    }
    this.selectie()
  }

  sleutelInklap() {
    return `tb-dicht-${this.templateValue}`
  }

  leesInklap() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.sleutelInklap()) || "[]")
    } catch (fout) {
      return []
    }
  }

  bewaarInklap(schema) {
    const id = schema.dataset.schemaId
    const dicht = new Set(this.leesInklap())
    if (schema.open) dicht.delete(id)
    else dicht.add(id)
    try {
      window.sessionStorage.setItem(this.sleutelInklap(), JSON.stringify([...dicht]))
    } catch (fout) {
      // privévenster: dan klapt alles gewoon weer open
    }
  }

  herstelInklap() {
    const dicht = new Set(this.leesInklap())
    this.element.querySelectorAll("details.tb-schema").forEach((schema) => {
      // Het schema met de actieve sessie blijft altijd open
      if (dicht.has(schema.dataset.schemaId) && !schema.classList.contains("is-actief")) schema.open = false
    })
  }

  // Een link uit de controle (stap 4) wijst naar een oefening: even markeren.
  springNaarAnker() {
    if (!window.location.hash.startsWith("#rij-")) return
    const rij = document.querySelector(window.location.hash)
    if (!rij) return
    rij.scrollIntoView({ block: "center" })
    rij.classList.add("is-gekozen")
    window.setTimeout(() => rij.classList.remove("is-gekozen"), 2500)
  }
}
